/**
 * Aspect — Wall collision (2D circle vs wall segments)
 * --------------------------------------------------------------
 * Resolves first-person movement against room wall segments. Door openings
 * subtract collision spans while the door is open; a closed door leaf adds
 * a blocking segment across the doorway.
 */

import { isArcWall, sampleWallPolyline, wallLength } from "./wall_geometry.js";
import { extentsXZ, blocksWalkCollision, OBSTACLE_PADDING } from "./furniture_placement.js";

const EPS = 1e-6;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
// Slight pad so the player capsule does not graze furniture corners.
const OBSTACLE_PAD = OBSTACLE_PADDING;
// Widen each doorway in the collision model so a player (radius ~0.28) walks
// through cleanly instead of snagging on the door jambs.
const DOOR_CLEARANCE = 0.34;

function closestOnSegment(ax, az, bx, bz, px, pz) {
  const abx = bx - ax;
  const abz = bz - az;
  const len2 = abx * abx + abz * abz;
  if (len2 < EPS) return { x: ax, z: az, t: 0 };
  const t = clamp(((px - ax) * abx + (pz - az) * abz) / len2, 0, 1);
  return { x: ax + t * abx, z: az + t * abz, t };
}

function pushOut(px, pz, radius, ax, az, bx, bz, halfThick) {
  const cp = closestOnSegment(ax, az, bx, bz, px, pz);
  const dx = px - cp.x;
  const dz = pz - cp.z;
  const dist = Math.hypot(dx, dz);
  const minDist = radius + halfThick;
  if (dist >= minDist - EPS) return { x: px, z: pz };
  if (dist < EPS) {
    // Degenerate: push along wall normal (best effort).
    const wx = bx - ax;
    const wz = bz - az;
    const nx = -wz;
    const nz = wx;
    const nlen = Math.hypot(nx, nz) || 1;
    return { x: px + (nx / nlen) * minDist, z: pz + (nz / nlen) * minDist };
  }
  const s = (minDist - dist) / dist;
  return { x: px + dx * s, z: pz + dz * s };
}

/** Push a circle out of an axis-aligned box (world XZ). */
function pushOutAABB(px, pz, radius, cx, cz, halfW, halfD) {
  const lx = px - cx;
  const lz = pz - cz;
  const closestX = clamp(lx, -halfW, halfW);
  const closestZ = clamp(lz, -halfD, halfD);
  const dx = lx - closestX;
  const dz = lz - closestZ;
  const distSq = dx * dx + dz * dz;
  const r2 = radius * radius;
  if (distSq >= r2 - EPS) return { x: px, z: pz };
  if (distSq < EPS) {
    const penX = halfW + radius - Math.abs(lx);
    const penZ = halfD + radius - Math.abs(lz);
    if (penX < penZ) {
      const sign = lx !== 0 ? Math.sign(lx) : 1;
      return { x: cx + sign * (halfW + radius), z: pz };
    }
    const sign = lz !== 0 ? Math.sign(lz) : 1;
    return { x: px, z: cz + sign * (halfD + radius) };
  }
  const dist = Math.sqrt(distSq);
  const s = (radius - dist) / dist;
  return { x: px + dx * s, z: pz + dz * s };
}

/** @typedef {{ ax:number, az:number, bx:number, bz:number, halfThick:number }} WallSeg */
/** @typedef {{ cx:number, cz:number, halfW:number, halfD:number }} ObstacleBox */

export class WallCollision {
  constructor() {
    /** @type {WallSeg[]} */
    this.segments = [];
    /** @type {ObstacleBox[]} */
    this.obstacles = [];
    this.bounds = null;
  }

  /**
   * @param {object} room serialized room
   * @param {import('../interaction/door_system.js').DoorSystem|null} doors
   */
  rebuild(room, doors = null) {
    this.segments = [];
    this.bounds = room?.bounds ?? null;
    if (!room?.walls) return;

    for (const wall of room.walls) {
      const halfThick = (wall.thickness ?? 0.15) / 2 + 0.02;
      const m = DOOR_CLEARANCE / (wallLength(wall) || 1);
      const gaps = (doors?.gapsForWall(wall.id) ?? []).map((g) => ({
        t0: g.t0 - m,
        t1: g.t1 + m,
      }));

      if (isArcWall(wall)) {
        const total = wallLength(wall);
        const { points } = sampleWallPolyline(wall, 0.35);
        let acc = 0;
        for (let i = 0; i < points.length - 1; i++) {
          const [ax, az] = points[i];
          const [bx, bz] = points[i + 1];
          const segLen = Math.hypot(bx - ax, bz - az) || 1;
          const g0 = acc / total;
          const g1 = (acc + segLen) / total;
          const segGaps = gaps
            .filter((g) => g.t1 > g0 && g.t0 < g1)
            .map((g) => ({
              t0: Math.max(0, (g.t0 - g0) / (g1 - g0)),
              t1: Math.min(1, (g.t1 - g0) / (g1 - g0)),
            }));
          this._addWallWithGaps(ax, az, bx, bz, halfThick, segGaps);
          acc += segLen;
        }
        continue;
      }

      const ax = wall.start[0];
      const az = wall.start[1];
      const bx = wall.end[0];
      const bz = wall.end[1];
      this._addWallWithGaps(ax, az, bx, bz, halfThick, gaps);
    }
  }

  /**
   * Rebuild furniture / placed-object blocking boxes from placement records.
   * Uses catalog footprint half-extents after yaw (same as placement gizmo).
   * @param {Iterable<object>} placements
   */
  rebuildObstacles(placements) {
    this.obstacles = [];
    for (const p of placements ?? []) {
      if (!blocksWalkCollision(p)) continue;
      const pos = p?.position;
      if (!pos) continue;
      const scale = p.scale ?? 1;
      const rotY = p.rotation_y ?? 0;
      const { ex, ez } = extentsXZ(p.footprint ?? { w: 1, d: 1 }, rotY, scale);
      this.obstacles.push({
        cx: pos[0],
        cz: pos[2],
        halfW: ex + OBSTACLE_PAD,
        halfD: ez + OBSTACLE_PAD,
      });
    }
  }

  /** Split a wall span by open-door gaps (each gap is t0..t1 along the segment). */
  _addWallWithGaps(ax, az, bx, bz, halfThick, gaps) {
    if (gaps.length === 0) {
      this.segments.push({ ax, az, bx, bz, halfThick });
      return;
    }
    const sorted = [...gaps].sort((a, b) => a.t0 - b.t0);
    let t = 0;
    for (const g of sorted) {
      const t0 = clamp(g.t0, 0, 1);
      const t1 = clamp(g.t1, 0, 1);
      if (t0 > t + EPS) {
        this._addPartial(ax, az, bx, bz, halfThick, t, t0);
      }
      t = Math.max(t, t1);
    }
    if (t < 1 - EPS) {
      this._addPartial(ax, az, bx, bz, halfThick, t, 1);
    }
  }

  _addPartial(ax, az, bx, bz, halfThick, t0, t1) {
    const x0 = ax + (bx - ax) * t0;
    const z0 = az + (bz - az) * t0;
    const x1 = ax + (bx - ax) * t1;
    const z1 = az + (bz - az) * t1;
    if (Math.hypot(x1 - x0, z1 - z0) < 0.05) return;
    this.segments.push({ ax: x0, az: z0, bx: x1, bz: z1, halfThick });
  }

  /**
   * Resolve a proposed XZ move for a circle of `radius`.
   * @returns {{ x:number, z:number }}
   */
  resolve(x, z, nx, nz, radius) {
    let px = nx;
    let pz = nz;

    if (this.bounds) {
      const { min, max } = this.bounds;
      px = clamp(px, min[0] + radius, max[0] - radius);
      pz = clamp(pz, min[2] + radius, max[2] - radius);
    }

    for (let pass = 0; pass < 4; pass++) {
      for (const s of this.segments) {
        const out = pushOut(px, pz, radius, s.ax, s.az, s.bx, s.bz, s.halfThick);
        px = out.x;
        pz = out.z;
      }
      for (const o of this.obstacles) {
        const out = pushOutAABB(px, pz, radius, o.cx, o.cz, o.halfW, o.halfD);
        px = out.x;
        pz = out.z;
      }
    }
    return { x: px, z: pz };
  }
}

export default WallCollision;
